import React from "react";
import { Trans } from "@lingui/macro";
import { getColor } from "components/utils";
import { typePagination } from "model/system/constants";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  DotsHorizontalIcon,
} from "@heroicons/react/solid";

const MIN_COUNT = "14";

const itemsPerPageList = [
  { id: MIN_COUNT, name: MIN_COUNT },
  { id: "25", name: "25" },
  { id: "50", name: "50" },
  { id: "100", name: "100" },
  // {id: 'all', name: '-'},
];

// Type
export type typePaginationDashboardObject = {
  paginationObject: typePagination;
  onGoToPage: (pageNumber: number) => void;
  onSelectItems: (itemsPerPage: number) => void;
};

export type typePaginationDashboard = {
  size?: "sm";
  variant?: string;
  pagination: typePaginationDashboardObject;
  buttonType?: React.ButtonHTMLAttributes<any>["type"];
};

const sizeClass = {
  sm: "px-3 py-1 text-xs",
};

export const PaginationDashboard: React.FC<typePaginationDashboard> = ({
  size,
  variant,
  buttonType,
  pagination,
}) => {
  const color = getColor(variant);

  const countButtons = 3;

  const links = [];

  let sizeClassName = "px-4 py-2 text-sm";
  if (size) {
    sizeClassName = sizeClass[size];
  }

  if (
    pagination.paginationObject.totalItems &&
    pagination.paginationObject.itemsPerPage
  ) {
    for (
      let i = 1;
      i <=
      Math.ceil(
        pagination.paginationObject.totalItems /
          pagination.paginationObject.itemsPerPage
      );
      i++
    ) {
      if (
        pagination.paginationObject.pageNumber - countButtons < i &&
        i < pagination.paginationObject.pageNumber + countButtons + 2
      ) {
        const pageClassName =
          pagination.paginationObject.pageNumber === i - 1
            ? `z-10 bg-${color}-50 border-${color}-500 text-${color}-600 relative inline-flex items-center border font-medium focus:outline-none ${sizeClassName}`
            : `bg-white border-gray-300 text-gray-500 hover:bg-gray-50 relative inline-flex items-center border font-medium focus:outline-none ${sizeClassName}`;

        const onClick =
          pagination.paginationObject.pageNumber === i - 1
            ? () => console.log()
            : () => pagination.onGoToPage(i - 1);

        links.push(
          <button
            key={i}
            type={buttonType}
            onClick={onClick}
            className={pageClassName}
          >
            {i}
          </button>
        );
      }
    }
  }

  const [itemsPerPage, setItemsPerPage] = React.useState(
    pagination.paginationObject.itemsPerPage || MIN_COUNT
  );
  const onSelectItemsPerPage = (e: React.FormEvent<HTMLSelectElement>) => {
    setItemsPerPage(e.currentTarget.value);
    // console.log(e.currentTarget.value)
    pagination.onSelectItems(
      e.currentTarget.value === "all"
        ? pagination.paginationObject.totalItems!
        : Number(e.currentTarget.value)
    );
  };

  const nestOnClick = () => {
    pagination.onGoToPage(pagination.paginationObject.pageNumber + 1);
  };
  const nextClassName = `relative inline-flex items-center border border-gray-300 font-medium rounded-md ${sizeClassName} text-gray-${
    !pagination.paginationObject.isLastPage ? "700 bg-white" : "400 bg-gray-50"
  } hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-${color}-700`;

  const previousOnClick = () => {
    pagination.onGoToPage(pagination.paginationObject.pageNumber - 1);
  };
  const previousClassName = `relative inline-flex items-center border border-gray-300 font-medium rounded-md ${sizeClassName} text-gray-${
    !pagination.paginationObject.isFirstPage ? "700 bg-white" : "400 bg-gray-50"
  } hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-${color}-700`;

  return (
    <>
      {pagination && pagination.paginationObject.totalItems ? (
        <div className="flex mt-4 items-center justify-between">
          <div className="flex-1 flex gap-4 justify-between sm:hidden">
            <button
              type={buttonType}
              onClick={previousOnClick}
              className={previousClassName}
              disabled={pagination.paginationObject.isFirstPage}
            >
              <Trans>Previous</Trans>
            </button>

            <button
              type={buttonType}
              onClick={nestOnClick}
              className={nextClassName}
              disabled={pagination.paginationObject.isLastPage}
            >
              <Trans>Next</Trans>
            </button>
          </div>

          <div className="hidden sm:flex-1 sm:flex sm:flex-wrap sm:gap-4 sm:gap-y-2 sm:items-center sm:justify-between">
            <div className="flex gap-4 items-center">
              <div>
                <p className="text-sm text-gray-700">
                  <Trans>
                    Items{" "}
                    <span className="font-medium">
                      {pagination.paginationObject.itemsPerPage
                        ? pagination.paginationObject.itemsPerPage! *
                            (pagination.paginationObject.pageNumber + 1) -
                          pagination.paginationObject.itemsPerPage! +
                          1
                        : 1}{" "}
                      -{" "}
                      {pagination.paginationObject.itemsPerPage! <
                      pagination.paginationObject.totalItems!
                        ? pagination.paginationObject.isLastPage
                          ? pagination.paginationObject.totalItems!
                          : pagination.paginationObject.itemsPerPage! *
                            (pagination.paginationObject.pageNumber + 1)
                        : pagination.paginationObject.totalItems!}
                    </span>{" "}
                    of{" "}
                    <span className="font-medium">
                      {pagination.paginationObject.totalItems}
                    </span>
                  </Trans>
                </p>
              </div>
              <div className="flex gap-2 items-center text-sm text-gray-700">
                <label
                  htmlFor="items_per_page"
                  className={"text-sm text-gray-700"}
                >
                  <Trans>items per page</Trans>
                </label>
                <select
                  id="items_per_page"
                  value={itemsPerPage}
                  onChange={(e) => onSelectItemsPerPage(e)}
                  className="px-2 py-0 pr-9 border border-gray-300 rounded focus:border-primary-700 focus:ring-primary-500"
                >
                  {itemsPerPageList.map((item, index) => {
                    return (
                      <option key={index} value={item.id}>
                        {item.name}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>

            <div>
              <nav
                className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                aria-label="Pagination"
              >
                <button
                  type={buttonType}
                  onClick={() =>
                    pagination.onGoToPage(
                      pagination.paginationObject.pageNumber - 1
                    )
                  }
                  disabled={pagination.paginationObject.isFirstPage}
                  className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 ${
                    pagination.paginationObject.isFirstPage
                      ? "bg-gray-50 cursor-not-allowed"
                      : "bg-white"
                  } text-sm font-medium text-gray-500 hover:bg-gray-50 focus:outline-none`}
                >
                  <span className="sr-only">
                    <Trans>Previous</Trans>
                  </span>
                  <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                </button>

                {pagination.paginationObject.pageNumber > countButtons ? (
                  <div
                    className={`relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500`}
                  >
                    <DotsHorizontalIcon className="h-5 w-5" />
                  </div>
                ) : null}

                {links}

                {pagination.paginationObject.pageNumber <
                pagination.paginationObject.pageCount! - countButtons ? (
                  <div
                    className={`relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500`}
                  >
                    <DotsHorizontalIcon className="h-5 w-5" />
                  </div>
                ) : null}

                <button
                  type={buttonType}
                  onClick={() =>
                    pagination.onGoToPage(
                      pagination.paginationObject.pageNumber + 1
                    )
                  }
                  disabled={pagination.paginationObject.isLastPage}
                  className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 ${
                    pagination.paginationObject.isLastPage
                      ? "bg-gray-50 cursor-not-allowed"
                      : "bg-white"
                  } text-sm font-medium text-gray-500 hover:bg-gray-50 focus:outline-none`}
                >
                  <span className="sr-only">
                    <Trans>Next</Trans>
                  </span>
                  <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};
